import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, 
  Car, 
  Plus, 
  Trash2, 
  AlertCircle, 
  Loader2,
  Copy,
  Download,
  Upload,
  FileText,
  File,
  CheckCircle2
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { createPageUrl } from "@/utils";
import { uploadFile } from "@/integrations/Core";

export default function BensVeiculos() {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [processedVehicles, setProcessedVehicles] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processingDetails, setProcessingDetails] = useState({});
  const [logs, setLogs] = useState([]);

  // Função para salvar log na pasta /logs
  const saveLog = async (action, data, response = null, error = null) => {
    try {
      const timestamp = new Date().toISOString();
      const logEntry = {
        timestamp,
        action,
        data,
        response,
        error: error ? error.toString() : null
      };
      
      // Adicionar ao estado de logs para exibição na UI (opcional)
      setLogs(prev => [...prev, logEntry]);
      
      // Enviar log para o servidor - formato antigo compatível com log.php
      const logData = {
        type: "DEBUG",
        message: `${action}: ${JSON.stringify(data).substring(0, 100)}...`,
        data: {
          action,
          details: data,
          response: response ? response : null,
          error: error ? error.toString() : null,
          timestamp
        }
      };
      
      console.log("Enviando log para servidor:", logData);
      
      // Fazer requisição para o endpoint de log (PHP)
      try {
        const logResponse = await fetch('/api/log.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(logData)
        });
        
        if (!logResponse.ok) {
          console.error('Erro ao salvar log no servidor:', await logResponse.text());
        } else {
          console.log(`Log salvo com sucesso: ${action}`);
        }
      } catch (serverLogError) {
        console.error('Exceção ao tentar salvar log no servidor:', serverLogError);
      }
    } catch (logError) {
      console.error('Erro ao preparar log:', logError);
    }
  };

  // Função para lidar com eventos de arrastar arquivos
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Função para lidar com soltar arquivos
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles]);
  };

  // Função para lidar com seleção de arquivos pelo input
  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  // Função para abrir o seletor de arquivos
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Função para remover um arquivo
  const removeFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  // Função para formatar o tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    else return (bytes / 1048576).toFixed(2) + ' MB';
  };

  // Função para extrair informações do documento via API
  const extractDocumentInfo = async (url, fileName) => {
    try {
      // Incluir timestamp na URL para evitar cache
      const noCacheUrl = `${url}?nocache=${new Date().getTime()}`;
      
      setProcessingDetails(prev => ({
        ...prev, 
        [fileName]: 'Enviando documento para o OCR...'
      }));
      
      // Endpoint do OCR na Replit
      const endpoint = 'https://document-ocr-extractor-contatoarthurol.replit.app/process-document';
      
      // Modelo para usar na extração
      const model = 'Qwen/Qwen2-VL-72B-Instruct';
      
      // Prompt para extração de informações
      const prompt = `Extraia os seguintes dados TODO EM MAIUSCULO, (OBS: FORMATO DA PLACA LLL-NLNN ONDE L É LETRA E N É NÚMERO, Importante: Quando não for encontrado alguma variável retorne com o valor 'NÃO INFORMADO'):\n
'RENAVAM:' {CÓDIGO RENAVAM}, 'DESCRICAO:' POSSUI UM VEICULO AUTOMOTOR DO TIPO: {ESPÉCIE / TIPO}, DA MARCA: {MARCA}, MODELO: {/ MODELO / VERSÃO}, ANO DE FABRICAÇÃO {ANO FABRICAÇÃO}, MODELO: {ANO MODELO}, PLACA: {PLACA}, COMBUSTÍVEL: {COMBUSTÍVEL}, DA COR {COR PREDOMINANTE}, MOTOR COM POTÊNCIA DE: {POTÊNCIA} E UMA POTENCIA CILINDRICA: {/CILINDRADA}CC.`;
      
      // Log da requisição do OCR
      await saveLog('REQUISICAO_OCR', {
        endpoint,
        request: {
          model,
          prompt,
          url: noCacheUrl
        },
        fileName
      });
      
      // Fazer a requisição para o OCR
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'fastapi_live_f6d0d199fa86abaa4d1169983c5f3f66'  // Token de autenticação adicionado
        },
        body: JSON.stringify({
          model,
          prompt,
          url
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        await saveLog('ERRO_OCR', {
          endpoint,
          request: {
            model,
            prompt,
            url: noCacheUrl
          },
          statusCode: response.status,
          fileName
        }, null, errorText);
        
        throw new Error(`Erro ao extrair informações do documento: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      
      // Registrar log da resposta bem-sucedida
      await saveLog('RESPOSTA_OCR_SUCESSO', {
        endpoint,
        request: {
          model,
          prompt,
          url: noCacheUrl
        },
        fileName
      }, data);
      
      // Verificar se o OCR retornou conteúdo válido
      if (!data || !data.content) {
        console.error('Resposta OCR sem conteúdo válido');
        throw new Error('Resposta OCR sem conteúdo válido');
      }
      
      // ******* LOGS DETALHADOS DO RENAVAM *******
      try {
        const content = data.content;
        
        // Log do conteúdo bruto para debug
        await saveLog('CONTENT_RAW_DEBUG', {
          content: content,
          contentLength: content.length,
          fileName
        });
        
        // Método 1: Buscar qualquer sequência de 11 números no conteúdo
        const renavamPattern = /\d{11}/;
        const renavamMatch = content.match(renavamPattern);
        
        await saveLog('RENAVAM_EXTRACT_DEBUG_PATTERN1', {
          pattern: renavamPattern.toString(),
          matchResult: renavamMatch ? JSON.stringify(renavamMatch) : "null",
          foundValue: renavamMatch ? renavamMatch[0] : "não encontrado",
          fileName
        });
        
        // Método 2: Buscar especificamente o padrão 'RENAVAM:' seguido de números
        const renavamPattern2 = /RENAVAM[:\s'"]*([0-9]+)/i;
        const renavamMatch2 = content.match(renavamPattern2);
        
        await saveLog('RENAVAM_EXTRACT_DEBUG_PATTERN2', {
          pattern: renavamPattern2.toString(),
          matchResult: renavamMatch2 ? JSON.stringify(renavamMatch2) : "null",
          foundValue: renavamMatch2 && renavamMatch2[1] ? renavamMatch2[1] : "não encontrado",
          fileName
        });
        
        // Método 3: Apenas para verificar, extrair todos os números do conteúdo
        const allNumbersPattern = /\d+/g;
        const allNumbersMatches = content.match(allNumbersPattern);
        
        await saveLog('RENAVAM_EXTRACT_DEBUG_ALLNUMBERS', {
          pattern: allNumbersPattern.toString(),
          matches: allNumbersMatches ? allNumbersMatches.slice(0, 5) : [], // Primeiros 5 números encontrados
          fileName
        });
        
        // Verificar também a função parseVehicleInfo que pode estar sobrescrevendo
        const renavamMatchInParse = content.match(/RENAVAM:\s*([^\s,;]+)/i);
        
        await saveLog('RENAVAM_EXTRACT_DEBUG_PARSEFUNCTION', {
          pattern: "/RENAVAM:\\s*([^\\s,;]+)/i",
          matchResult: renavamMatchInParse ? JSON.stringify(renavamMatchInParse) : "null",
          foundValue: renavamMatchInParse && renavamMatchInParse[1] ? renavamMatchInParse[1] : "não encontrado",
          fileName
        });
      } catch (debugError) {
        console.error('Erro nos logs de debug do RENAVAM:', debugError);
        await saveLog('RENAVAM_EXTRACT_DEBUG_ERROR', {
          error: debugError.toString(),
          fileName
        });
      }
      // ******* FIM DOS LOGS DETALHADOS *******
      
      return data.content;
    } catch (error) {
      console.error('Erro ao extrair informações do documento:', error);
      throw error;
    }
  };

  // Função para extrair as informações do veículo do resultado do OCR
  const extractVehicleInfo = (result) => {
    try {
      if (!result || typeof result !== 'object' || !result.choices || result.choices.length === 0) {
        console.error('Formato de resultado inválido:', result);
        return null;
      }
      
      const content = result.choices[0]?.message?.content || '';
      
      if (!content) {
        console.error('Conteúdo vazio na resposta do OCR');
        return null;
      }
      
      console.log('Conteúdo extraído:', content);

      // Log adicional para debug - registra o conteúdo completo para cada arquivo
      saveLog('CONTEUDO_EXTRAIDO_OCR_RAW', { content }, null, null).catch(e => console.error('Erro ao logar conteúdo:', e));
      
      // NOVA ABORDAGEM: Buscar diretamente uma sequência de 11 números no início do conteúdo
      let renavam = 'NÃO INFORMADO';
      
      // Método 1: Buscar qualquer sequência de 11 números no conteúdo
      const renavamPattern = /\d{11}/;
      const renavamMatch = content.match(renavamPattern);
      
      if (renavamMatch && renavamMatch[0]) {
        renavam = renavamMatch[0];
        console.log('RENAVAM extraído (sequência de 11 números):', renavam);
        // Log adicional para debug
        saveLog('RENAVAM_EXTRAIDO', { metodo: 'sequencia_11_numeros', renavam }, null, null).catch(e => console.error('Erro ao logar RENAVAM:', e));
      } else {
        // Método 2: Buscar qualquer sequência de números próxima à palavra RENAVAM
        const renavamContextPattern = /RENAVAM[^0-9]*(\d+)/i;
        const renavamContextMatch = content.match(renavamContextPattern);
        
        if (renavamContextMatch && renavamContextMatch[1]) {
          renavam = renavamContextMatch[1].trim();
          console.log('RENAVAM extraído (próximo à palavra RENAVAM):', renavam);
          // Log adicional para debug
          saveLog('RENAVAM_EXTRAIDO', { metodo: 'proximo_palavra_renavam', renavam }, null, null).catch(e => console.error('Erro ao logar RENAVAM:', e));
        } else {
          // Método 3: Extrair os primeiros números encontrados (que geralmente é o RENAVAM)
          const numbersPattern = /\d+/;
          const numbersMatch = content.match(numbersPattern);
          
          if (numbersMatch && numbersMatch[0]) {
            renavam = numbersMatch[0];
            console.log('RENAVAM extraído (primeiros números encontrados):', renavam);
            // Log adicional para debug
            saveLog('RENAVAM_EXTRAIDO', { metodo: 'primeiros_numeros', renavam }, null, null).catch(e => console.error('Erro ao logar RENAVAM:', e));
          } else {
            console.log('Nenhum padrão de RENAVAM encontrado. Conteúdo de análise:', content.substring(0, 100));
            // Log adicional para debug
            saveLog('RENAVAM_NAO_ENCONTRADO', { conteudo_parcial: content.substring(0, 100) }, null, null).catch(e => console.error('Erro ao logar falha na extração de RENAVAM:', e));
          }
        }
      }
      
      // Regex para extrair a placa
      const placaRegex = /PLACA:\s*([A-Z0-9-]+)/i;
      const placaMatch = content.match(placaRegex);
      const placa = placaMatch ? placaMatch[1].trim() : 'NÃO INFORMADO';
      
      // Extrair a descrição do veículo
      const descricaoRegex = /'DESCRICAO:'(.*?)(?='|$)/is;
      const descricaoMatch = content.match(descricaoRegex);
      let descricaoOriginal = descricaoMatch ? descricaoMatch[1].trim() : 'VEÍCULO AUTOMOTOR.';
      
      // Nova abordagem para processar a descrição de trás para frente
      let descricaoLimpa = '';
      
      try {
        console.log('Descrição original:', descricaoOriginal);
        
        // Verificar se a descrição original tem conteúdo
        if (descricaoOriginal && descricaoOriginal.trim()) {
          // Garantir que a descrição termina com um ponto para consistência
          if (!descricaoOriginal.endsWith('.')) {
            descricaoOriginal += '.';
          }
          
          // Função para verificar se um texto contém "NÃO INFORMADO" (case insensitive)
          const contemNaoInformado = (texto) => {
            return /não\s*informado/i.test(texto);
          };
          
          // Dividir a descrição em seções baseadas no ponto final
          const partes = descricaoOriginal.split('.');
          
          // Pegar a parte principal (antes do ponto final)
          let partePrincipal = partes[0];
          
          // Processamento de trás para frente conforme solicitado
          // Dividir a parte principal em seções baseadas em vírgulas ou "E"
          const secoes = [];
          let textoRestante = partePrincipal;
          
          // Dividir em seções - primeiro pela conjunção "E" e depois por vírgulas
          const secoesComE = textoRestante.split(' E ');
          
          // Processar a última parte (após o último "E", se existir)
          let parteProcessada = secoesComE[secoesComE.length - 1];
          const secoesComVirgula = parteProcessada.split(',');
          
          // Processar de trás para frente, começando pela última seção
          const secoesFinais = [];
          
          // Processar as seções com vírgula (de trás para frente)
          for (let i = secoesComVirgula.length - 1; i >= 0; i--) {
            const secaoAtual = secoesComVirgula[i].trim();
            
            // Verificar se contém "NÃO INFORMADO"
            if (contemNaoInformado(secaoAtual)) {
              console.log(`Removendo seção com "NÃO INFORMADO": "${secaoAtual}"`);
              continue; // Pular esta seção
            }
            
            // Adicionar no início da lista (para manter a ordem original)
            secoesFinais.unshift(secaoAtual);
          }
          
          // Reconstruir a última parte
          parteProcessada = secoesFinais.join(', ');
          
          // Substituir a última parte no array de seções com E
          const secoesFinaisComE = [];
          
          // Se existe parte processada com vírgulas, adiciona-la
          if (parteProcessada) {
            secoesFinaisComE.push(parteProcessada);
          }
          
          // Processar as partes anteriores que tinham "E" (de trás para frente)
          for (let i = secoesComE.length - 2; i >= 0; i--) {
            const secaoAtual = secoesComE[i].trim();
            
            // Verificar se contém "NÃO INFORMADO"
            if (contemNaoInformado(secaoAtual)) {
              console.log(`Removendo seção com "E" e "NÃO INFORMADO": "${secaoAtual}"`);
              continue; // Pular esta seção
            }
            
            // Processar sub-seções com vírgula nesta parte com "E"
            const subSecoes = secaoAtual.split(',');
            const subSecoesLimpas = [];
            
            for (let j = subSecoes.length - 1; j >= 0; j--) {
              const subSecaoAtual = subSecoes[j].trim();
              
              if (contemNaoInformado(subSecaoAtual)) {
                console.log(`Removendo sub-seção com "NÃO INFORMADO": "${subSecaoAtual}"`);
                continue;
              }
              
              subSecoesLimpas.unshift(subSecaoAtual);
            }
            
            // Adicionar as sub-seções limpas
            if (subSecoesLimpas.length > 0) {
              // Adicionar no início para manter a ordem original
              secoesFinaisComE.unshift(subSecoesLimpas.join(', '));
            }
          }
          
          // Reconstruir a descrição final
          descricaoLimpa = secoesFinaisComE.join(', ');
          
          // Implementação da lógica específica conforme solicitado pelo usuário
          // Esta implementação alternativa funciona diretamente com o conteúdo completo
          const processarConteudoDeTrasPraFrente = (texto) => {
            if (!texto) return '';
            
            // Garantir que termina com ponto
            if (!texto.endsWith('.')) {
              texto += '.';
            }
            
            // Capturar tudo até o ponto final
            const conteudoPrincipal = texto.substring(0, texto.lastIndexOf('.'));
            const resultado = [];
            
            // Dividir por vírgulas para analisar seções
            const secoes = conteudoPrincipal.split(',');
            
            // Iniciar do final (última seção)
            for (let i = secoes.length - 1; i >= 0; i--) {
              let secaoAtual = secoes[i].trim();
              
              // Verificar se é uma seção com "E" que precisa de processamento especial
              if (i > 0 && secaoAtual.startsWith('E ')) {
                // Esta é uma seção que começa com "E", verificar se contém "NÃO INFORMADO"
                if (contemNaoInformado(secaoAtual)) {
                  console.log(`Removendo seção com "E" e "NÃO INFORMADO": "${secaoAtual}"`);
                  continue; // Pular esta seção
                }
              }
              
              // Para outras seções regulares
              if (contemNaoInformado(secaoAtual)) {
                console.log(`Removendo seção regular com "NÃO INFORMADO": "${secaoAtual}"`);
                continue; // Pular esta seção
              }
              
              // Adicionar no início para manter ordem original
              resultado.unshift(secaoAtual);
            }
            
            // Reconstruir o texto com as seções restantes
            return resultado.join(', ') + '.';
          };
          
          // Usar a implementação alternativa que começa do final
          descricaoLimpa = processarConteudoDeTrasPraFrente(descricaoOriginal);
          
          // Limpar e formatar o resultado final
          descricaoLimpa = descricaoLimpa
            .replace(/,\s*,/g, ',') // Remover vírgulas duplicadas
            .replace(/^,\s*/, '') // Remover vírgulas no início
            .replace(/,\s*\./g, '.') // Remover vírgulas antes do ponto final
            .replace(/\s+/g, ' ') // Remover espaços extras
            .trim();
          
          console.log('Descrição limpa:', descricaoLimpa);
        } else {
          descricaoLimpa = 'POSSUI UM VEICULO AUTOMOTOR.';
        }
      } catch (error) {
        console.error('Erro ao processar descrição:', error);
        descricaoLimpa = descricaoOriginal;
        // Garantir que a descrição tem um ponto final
        if (!descricaoLimpa.endsWith('.')) {
          descricaoLimpa += '.';
        }
      }
      
      // Se a descrição ficou vazia após a limpeza, usar padrão
      if (!descricaoLimpa.trim()) {
        descricaoLimpa = 'POSSUI UM VEICULO AUTOMOTOR.';
      }
      
      // Garantir que a descrição termina com ponto
      if (!descricaoLimpa.endsWith('.')) {
        descricaoLimpa += '.';
      }
      
      // Usar a descrição limpa
      let descricao = descricaoLimpa;
      
      // Função para extrair um valor específico da descrição
      const extrairValor = (texto, chave) => {
        const regex = new RegExp(`${chave}\\s*([^,]+)`, 'i');
        const match = texto.match(regex);
        return match ? match[1].trim() : 'NÃO INFORMADO';
      };
      
      // Extrair informações do veículo
      const marca = extrairValor(descricao, 'MARCA:');
      const modelo = extrairValor(descricao, 'MODELO:');
      const anoFabricacao = extrairValor(descricao, 'ANO DE FABRICAÇÃO:');
      const anoModelo = extrairValor(descricao, 'MODELO:');
      
      return {
        renavam,
        placa,
        descricao: descricao.startsWith('POSSUI UM VEICULO AUTOMOTOR') 
                 ? descricao 
                 : `POSSUI UM VEICULO AUTOMOTOR - ${descricao}`,
        marca,
        modelo,
        anoFabricacao,
        anoModelo
      };
    } catch (error) {
      console.error('Erro ao extrair informações do veículo:', error);
      return null;
    }
  };

  // Função para fazer upload dos arquivos
  const uploadFiles = async () => {
    setError(null);
    setUploading(true);
    setProcessedVehicles([]);
    
    try {
      // Limpar o progresso de upload
      setUploadProgress({});
      
      // Array para armazenar os resultados
      const uploadResults = [];
      
      // Upload de cada arquivo
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress((prev) => ({ ...prev, [i]: 0 }));

        try {
          // Upload do arquivo
          const result = await uploadFile(file, (progress) => {
            setUploadProgress((prev) => ({
              ...prev,
              [i]: progress,
            }));
          });

          // Log detalhado do resultado do upload
          await saveLog('UPLOAD_RESULTADO_DETALHADO', {
            fileName: file.name,
            result: result
          });

          // Obter a URL completa do arquivo
          let fileUrl = result.full_url || result.fileUrl;
          if (!fileUrl && result.fileName) {
            fileUrl = `/files/${result.fileName}`;
          }
          if (!fileUrl && result.filename) {
            fileUrl = `/files/${result.filename}`;
          }

          // Log da URL que será usada
          await saveLog('URL_ARQUIVO_FINAL', {
            fileName: file.name,
            fileUrl: fileUrl
          });

          // Adicionar resultado ao array
          uploadResults.push({
            fileName: file.name,
            fileUrl: fileUrl,
            fileSize: file.size,
            status: 'success'
          });

          // Adicionar ao array de veículos processados
          setProcessedVehicles((prev) => [
            ...prev,
            {
              fileName: file.name,
              status: 'sucesso',
              mensagem: 'Arquivo enviado com sucesso'
            }
          ]);
        } catch (error) {
          console.error(`Erro no upload do arquivo ${file.name}:`, error);
          
          // Adicionar erro ao array de processados
          setProcessedVehicles((prev) => [
            ...prev,
            {
              fileName: file.name,
              status: 'erro',
              mensagem: `Erro no upload: ${error.message}`
            }
          ]);
        }
      }

      // Log dos resultados finais do upload
      await saveLog('RESULTADO_UPLOAD_FINAL', {
        resultados: uploadResults
      });

      return uploadResults;
    } catch (error) {
      console.error('Erro ao processar uploads:', error);
      setError(`Erro ao processar uploads: ${error.message}`);
      return [];
    } finally {
      setUploading(false);
    }
  };

  // Função para buscar o valor do veículo usando a API Brasil
  const getVehicleValue = async (marca, modelo, anoFabricacao, anoModelo, placa) => {
    try {
      // Verificar se a placa está disponível
      if (!placa || placa === "NÃO INFORMADO") {
        console.log("Placa não informada, não é possível consultar valor do veículo.");
        await saveLog('PLACA_NAO_INFORMADA', {
          veiculo: { marca, modelo, anoFabricacao, anoModelo, placa }
        });
        return { situacao_2023: "0,00", situacao_2024: "0,00" };
      }
      
      setProcessingDetails(prev => ({...prev, 'valor': `Buscando valor do veículo ${marca} ${modelo} - Placa: ${placa}...`}));
      
      const requestData = {
        tipo: "fipe",
        placa: placa.replace(/[^a-zA-Z0-9]/g, '') // Remover caracteres especiais da placa
      };
      
      // Token Bearer da API Brasil
      const bearerToken = "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwczovL2dhdGV3YXkuYXBpYnJhc2lsLmlvL2FwaS92Mi9hdXRoL3JlZ2lzdGVyIiwiaWF0IjoxNzM4MjM5OTc4LCJleHAiOjE3Njk3NzU5NzgsIm5iZiI6MTczODIzOTk3OCwianRpIjoiempUeDVqNXRJdmw5Q0twNSIsInN1YiI6IjEzNDc1IiwicHJ2IjoiMjNiZDVjODk0OWY2MDBhZGIzOWU3MDFjNDAwODcyZGI3YTU5NzZmNyJ9.ZX3iQRr0BK3j8r-qkHz1JyKJ0eD2o68I8PV3E2h_paA";
      
      // Registrar log da requisição
      await saveLog('REQUISICAO_VALOR_APIBRASIL', {
        endpoint: 'https://gateway.apibrasil.io/api/v2/vehicles/base/001/consulta',
        request: requestData,
        veiculo: { marca, modelo, anoFabricacao, anoModelo, placa }
      });
      
      try {
        const response = await fetch('https://gateway.apibrasil.io/api/v2/vehicles/base/001/consulta', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${bearerToken}`
          },
          body: JSON.stringify(requestData)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          await saveLog('ERRO_API_BRASIL', {
            endpoint: 'https://gateway.apibrasil.io/api/v2/vehicles/base/001/consulta',
            request: requestData,
            statusCode: response.status,
            veiculo: { marca, modelo, anoFabricacao, anoModelo, placa }
          }, null, errorText);
          
          console.error(`Erro ao consultar API Brasil: ${response.status} - ${errorText}`);
          return { situacao_2023: "0,00", situacao_2024: "0,00" };
        }
        
        const data = await response.json();
        
        // Registrar log da resposta bem-sucedida
        await saveLog('RESPOSTA_API_BRASIL', {
          endpoint: 'https://gateway.apibrasil.io/api/v2/vehicles/base/001/consulta',
          request: requestData,
          veiculo: { marca, modelo, anoFabricacao, anoModelo, placa }
        }, data);
        
        // Verificar se houve erro na resposta - Verifica especificamente data.error e a existência de data.data
        if (data.error === true || !data.data || !Array.isArray(data.data) || data.data.length === 0) {
          await saveLog('ERRO_RESPOSTA_API_BRASIL', {
            mensagem: data.message || 'Dados inválidos ou vazios',
            veiculo: { marca, modelo, anoFabricacao, anoModelo, placa }
          }, data);
          
          console.log('Resposta inválida da API Brasil, tentando consulta alternativa via IA...');
          
          // Implementação da consulta alternativa via IA
          try {
            // Atualizar status de processamento
            setProcessingDetails(prev => ({...prev, 'valor': `API Brasil falhou. Consultando IA para obter valor do veículo ${marca} ${modelo}...`}));
            
            // Endpoint da IA para consulta alternativa
            const iaEndpoint = 'https://document-ocr-extractor-contatoarthurol.replit.app/query-llm';
            
            // Preparar a consulta
            const anoModeloFormatado = anoModelo !== "NÃO INFORMADO" ? anoModelo : anoFabricacao;
            const searchQuery = `Qual o valor médio do carro seminovo com mais de 50mil km rodados DA MARCA: ${marca}, MODELO: ${modelo}, ANO DE FABRICAÇÃO ${anoFabricacao}, MODELO: ${anoModeloFormatado}?\n\nSeja objetivo e direto e me retorne apenas o valor médio`;
            
            // Log da consulta à IA
            await saveLog('CONSULTA_ALTERNATIVA_IA', {
              endpoint: iaEndpoint,
              query: searchQuery,
              veiculo: { marca, modelo, anoFabricacao, anoModelo, placa }
            });
            
            // Fazer a requisição para a IA
            const iaResponse = await fetch(iaEndpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': 'fastapi_live_f6d0d199fa86abaa4d1169983c5f3f66'
              },
              body: JSON.stringify({
                search: searchQuery
              })
            });
            
            if (!iaResponse.ok) {
              const errorText = await iaResponse.text();
              throw new Error(`Erro na consulta à IA: ${iaResponse.status} - ${errorText}`);
            }
            
            const iaData = await iaResponse.json();
            
            // Log da resposta da IA
            await saveLog('RESPOSTA_ALTERNATIVA_IA', {
              veiculo: { marca, modelo, anoFabricacao, anoModelo, placa }
            }, iaData);
            
            // Extrair o valor monetário da resposta
            const valorExtraido = extrairValorMonetario(iaData.content || '');
            
            // Log do valor extraído
            await saveLog('VALOR_EXTRAIDO_IA', {
              valorOriginal: iaData.content,
              valorExtraido,
              veiculo: { marca, modelo, anoFabricacao, anoModelo, placa }
            });
            
            console.log(`Valor obtido via IA para ${marca} ${modelo}: ${valorExtraido}`);
            
            // Retornar o valor para os dois anos (assumindo o mesmo valor)
            return { 
              situacao_2023: valorExtraido, 
              situacao_2024: valorExtraido 
            };
          } catch (iaError) {
            console.error('Erro ao consultar IA para obter valor alternativo:', iaError);
            
            // Log do erro na consulta à IA
            await saveLog('ERRO_CONSULTA_IA', {
              erro: iaError.toString(),
              veiculo: { marca, modelo, anoFabricacao, anoModelo, placa }
            });
            
            // Falha total, retornar zeros
            return { situacao_2023: "0,00", situacao_2024: "0,00" };
          }
        }
        
        // Verificar se há histórico disponível - lógica corrigida para considerar a possibilidade de histórico vazio
        if (!data.data[0].historico || !Array.isArray(data.data[0].historico)) {
          data.data[0].historico = []; // Inicializa como array vazio se não existir
        }
        
        // Se o histórico existe, mas está vazio, use o valor principal
        let valorAtual = 0;
        if (data.data[0].historico.length > 0) {
          valorAtual = data.data[0].historico[0].valor || 0;
        } else if (data.data[0].valor) {
          // Se não há histórico, use o valor principal (se existir)
          valorAtual = data.data[0].valor;
        }
        
        // Formatar os valores para o padrão brasileiro
        const formatarValor = (valor) => {
          if (!valor) return "0,00";
          return parseFloat(valor).toLocaleString('pt-BR', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          });
        };
        
        const valorFormatado = formatarValor(valorAtual);
        
        // Adicionar log detalhado sobre o valor encontrado
        console.log(`Valor do veículo ${marca} ${modelo} (placa: ${placa}): ${valorFormatado}`);
        
        await saveLog('VALOR_VEICULO_ENCONTRADO_API_BRASIL', {
          veiculo: { marca, modelo, anoFabricacao, anoModelo, placa },
          valor: valorFormatado,
          dadosCompletos: data.data[0]
        });
        
        return { 
          situacao_2023: valorFormatado, 
          situacao_2024: valorFormatado
        };
        
      } catch (error) {
        console.error('Erro ao consultar valor do veículo na API Brasil:', error);
        await saveLog('ERRO_CONSULTA_API_BRASIL', {
          veiculo: { marca, modelo, anoFabricacao, anoModelo, placa },
          erro: error.toString()
        }, null, error.toString());
        
        return { situacao_2023: "0,00", situacao_2024: "0,00" };
      }
    } catch (error) {
      console.error('Erro geral ao buscar valor do veículo:', error);
      return { situacao_2023: "0,00", situacao_2024: "0,00" };
    }
  };

  // Função para extrair valor monetário do conteúdo - versão melhorada para lidar com diversos formatos de resposta da IA
  const extrairValorMonetario = (content) => {
    if (!content) return "0,00";
    
    try {
      console.log('Extraindo valor monetário do texto:', content);
      
      // Padrões para valores monetários em diferentes formatos
      const patterns = [
        /R\$\s*([\d.,]+)/i,                     // R$ 50.000,00
        /valor\s*[de]*\s*R\$\s*([\d.,]+)/i,     // valor de R$ 50.000,00
        /preço\s*[de]*\s*R\$\s*([\d.,]+)/i,     // preço de R$ 50.000,00
        /custa\s*R\$\s*([\d.,]+)/i,             // custa R$ 50.000,00
        /em\s+torno\s+de\s+R\$\s*([\d.,]+)/i,   // em torno de R$ 50.000,00
        /aproximadamente\s+R\$\s*([\d.,]+)/i,   // aproximadamente R$ 50.000,00
        /cerca\s+de\s+R\$\s*([\d.,]+)/i,        // cerca de R$ 50.000,00
        /média\s+de\s+R\$\s*([\d.,]+)/i,        // média de R$ 50.000,00
        /entre\s+R\$\s*([\d.,]+)/i,             // entre R$ 50.000,00
        /varia\s+de\s+R\$\s*([\d.,]+)/i,        // varia de R$ 50.000,00
        /é\s+de\s+R\$\s*([\d.,]+)/i,            // é de R$ 50.000,00
        /estimado\s+em\s+R\$\s*([\d.,]+)/i,     // estimado em R$ 50.000,00
        /avaliado\s+em\s+R\$\s*([\d.,]+)/i,     // avaliado em R$ 50.000,00
        /([\d.,]+)/                             // Apenas números como último recurso
      ];
      
      // Log para depuração
      console.log('Iniciando extração com', patterns.length, 'padrões de expressão regular');
      
      for (const [index, pattern] of patterns.entries()) {
        const match = content.match(pattern);
        console.log(`Tentando padrão ${index + 1}:`, pattern.toString());
        
        if (match && match[1]) {
          console.log(`Padrão ${index + 1} encontrou:`, match[1]);
          
          // Limpar o valor encontrado e formatar corretamente
          let rawValue = match[1].trim();
          
          // Lidar com diferentes formatos de valor
          // Verificar se tem ponto/vírgula para decidir estratégia de conversão
          if (rawValue.includes(',') || rawValue.includes('.')) {
            // Remover pontos de milhar e usar vírgula como separador decimal
            rawValue = rawValue.replace(/\./g, '').replace(',', '.');
          }
          
          // Converter para número
          const numericValue = parseFloat(rawValue);
          
          if (!isNaN(numericValue) && numericValue > 0) {
            // Formatar o valor para o formato monetário brasileiro
            const formattedValue = numericValue.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
            
            console.log('Valor extraído e formatado:', formattedValue);
            return formattedValue;
          }
        }
      }
      
      console.log('Nenhum valor monetário encontrado no texto');
      return "0,00"; // Valor padrão se nenhum padrão for encontrado
    } catch (error) {
      console.error('Erro ao extrair valor monetário:', error);
      return "0,00"; // Valor padrão em caso de erro
    }
  };

  // Função para analisar as informações do veículo
  const parseVehicleInfo = (content) => {
    if (!content) return {};
    
    try {
      console.log('Conteúdo original do OCR:', content);
      
      // LOGS DETALHADOS PARA DIAGNÓSTICO DE RENAVAM
      saveLog('PARSE_VEHICLE_INFO_DEBUG', {
        contentStart: content.substring(0, 100),
        contentLength: content.length
      }).catch(e => console.error('Erro ao logar conteúdo de parseVehicleInfo:', e));
      
      // Extração de RENAVAM - Nova lógica prioritária
      let renavam = 'NÃO INFORMADO';
      
      // Método 1: Buscar uma sequência exata de 11 dígitos (formato padrão do RENAVAM)
      const renavamDigitsPattern = /\d{11}/;
      const renavamDigitsMatch = content.match(renavamDigitsPattern);
      
      if (renavamDigitsMatch && renavamDigitsMatch[0]) {
        renavam = renavamDigitsMatch[0];
        saveLog('PARSE_VEHICLE_RENAVAM_METHOD', {
          method: '11_digits_pattern',
          renavam: renavam
        }).catch(e => console.error('Erro ao logar método de extração:', e));
      } 
      else {
        // Método 2: Buscar na forma de "RENAVAM: número"
        const renavamLabeledPattern = /RENAVAM[:\s'"]*([0-9]+)/i;
        const renavamLabeledMatch = content.match(renavamLabeledPattern);
        
        if (renavamLabeledMatch && renavamLabeledMatch[1]) {
          renavam = renavamLabeledMatch[1];
          saveLog('PARSE_VEHICLE_RENAVAM_METHOD', {
            method: 'labeled_pattern',
            renavam: renavam
          }).catch(e => console.error('Erro ao logar método de extração:', e));
        }
        else {
          // Método 3: Extrair apenas números e considerar o primeiro (tentativa de último recurso)
          const allNumbersPattern = /\d+/;
          const allNumbersMatch = content.match(allNumbersPattern);
          
          if (allNumbersMatch && allNumbersMatch[0]) {
            renavam = allNumbersMatch[0];
            saveLog('PARSE_VEHICLE_RENAVAM_METHOD', {
              method: 'first_number',
              renavam: renavam
            }).catch(e => console.error('Erro ao logar método de extração:', e));
          }
        }
      }
      
      // Extração do resto das informações
      const tipoMatch = content.match(/TIPO:\s*([^,;]+)/i);
      const marcaMatch = content.match(/MARCA:\s*([^,;]+)/i);
      const modeloMatch = content.match(/MODELO:\s*([^,;]+)/i);
      const anoFabricacaoMatch = content.match(/FABRICAÇÃO\s*([^,;]+)/i);
      const anoModeloMatch = content.match(/ANO MODELO:\s*([^,;]+)/i);
      const placaMatch = content.match(/PLACA:\s*([^,;]+)/i);
      const combustivelMatch = content.match(/COMBUSTÍVEL:\s*([^,;]+)/i);
      const corMatch = content.match(/COR\s*([^,;]+)/i);
      const potenciaMatch = content.match(/POTÊNCIA DE:\s*([^,;]+)/i);
      const cilindradaMatch = content.match(/CILINDRICA:\s*([^,;]+)/i);
      
      // Logar extração final de RENAVAM antes de criar o objeto
      saveLog('PARSE_VEHICLE_FINAL_RENAVAM', {
        finalRenavam: renavam,
        originalContent: content.substring(0, 50)
      }).catch(e => console.error('Erro ao logar RENAVAM final:', e));
      
      // Criar o objeto de informações
      const vehicleInfo = {
        renavam: renavam, // Usar valor processado acima em vez de regex antigo
        tipo: tipoMatch ? tipoMatch[1].trim() : "NÃO INFORMADO",
        marca: marcaMatch ? marcaMatch[1].trim() : "NÃO INFORMADO",
        modelo: modeloMatch ? modeloMatch[1].trim() : "NÃO INFORMADO",
        anoFabricacao: anoFabricacaoMatch ? anoFabricacaoMatch[1].trim() : "NÃO INFORMADO",
        anoModelo: anoModeloMatch ? anoModeloMatch[1].trim() : "NÃO INFORMADO",
        placa: placaMatch ? placaMatch[1].trim() : "NÃO INFORMADO",
        combustivel: combustivelMatch ? combustivelMatch[1].trim() : "NÃO INFORMADO",
        cor: corMatch ? corMatch[1].trim() : "NÃO INFORMADO",
        potencia: potenciaMatch ? potenciaMatch[1].trim() : "NÃO INFORMADO",
        cilindrada: cilindradaMatch ? cilindradaMatch[1].trim() : "NÃO INFORMADO"
      };
      
      // Extrair a descrição diretamente do conteúdo do OCR
      const descricaoRegex = /'DESCRICAO:'(.*?)(?='|$)/is;
      const descricaoMatch = content.match(descricaoRegex);
      let descricaoOriginal = descricaoMatch ? descricaoMatch[1].trim() : '';
      
      // Se não tiver descrição original, criar a partir das informações extraídas
      if (!descricaoOriginal) {
        descricaoOriginal = "POSSUI UM VEICULO AUTOMOTOR";
        
        // Adicionar informações disponíveis
        const infoPartes = [];
        if (vehicleInfo.tipo !== "NÃO INFORMADO") infoPartes.push(`DO TIPO: ${vehicleInfo.tipo}`);
        if (vehicleInfo.marca !== "NÃO INFORMADO") infoPartes.push(`DA MARCA: ${vehicleInfo.marca}`);
        if (vehicleInfo.modelo !== "NÃO INFORMADO") infoPartes.push(`MODELO: ${vehicleInfo.modelo}`);
        if (vehicleInfo.anoFabricacao !== "NÃO INFORMADO") infoPartes.push(`ANO DE FABRICAÇÃO ${vehicleInfo.anoFabricacao}`);
        if (vehicleInfo.anoModelo !== "NÃO INFORMADO") infoPartes.push(`MODELO: ${vehicleInfo.anoModelo}`);
        if (vehicleInfo.placa !== "NÃO INFORMADO") infoPartes.push(`PLACA: ${vehicleInfo.placa}`);
        if (vehicleInfo.combustivel !== "NÃO INFORMADO") infoPartes.push(`COMBUSTÍVEL: ${vehicleInfo.combustivel}`);
        if (vehicleInfo.cor !== "NÃO INFORMADO") infoPartes.push(`DA COR ${vehicleInfo.cor}`);
        if (vehicleInfo.potencia !== "NÃO INFORMADO") infoPartes.push(`MOTOR COM POTÊNCIA DE: ${vehicleInfo.potencia}`);
        if (vehicleInfo.cilindrada !== "NÃO INFORMADO") infoPartes.push(`E UMA POTENCIA CILINDRICA: ${vehicleInfo.cilindrada} CC.`);
        
        // Juntar as partes disponíveis
        if (infoPartes.length > 0) {
          descricaoOriginal += ", " + infoPartes.join(", ");
        }
      }
      
      console.log('Descrição original montada:', descricaoOriginal);
      
      // Aplicar a lógica de processamento de trás para frente para remover "NÃO INFORMADO"
      let descricaoLimpa = '';
      
      try {
        // Garantir que a descrição termina com um ponto para consistência
        if (!descricaoOriginal.endsWith('.')) {
          descricaoOriginal += '.';
        }
        
        // Função para verificar se um texto contém "NÃO INFORMADO" (case insensitive)
        const contemNaoInformado = (texto) => {
          return /não\s*informado/i.test(texto);
        };
        
        // Implementação da lógica específica conforme solicitado pelo usuário
        // Esta implementação funciona diretamente com o conteúdo completo
        const processarConteudoDeTrasPraFrente = (texto) => {
          if (!texto) return '';
          
          // Garantir que termina com ponto
          if (!texto.endsWith('.')) {
            texto += '.';
          }
          
          // Capturar tudo até o ponto final
          const conteudoPrincipal = texto.substring(0, texto.lastIndexOf('.'));
          const resultado = [];
          
          // Dividir por vírgulas para analisar seções
          const secoes = conteudoPrincipal.split(',');
          
          // Iniciar do final (última seção)
          for (let i = secoes.length - 1; i >= 0; i--) {
            let secaoAtual = secoes[i].trim();
            
            // Verificar se é uma seção com "E" que precisa de processamento especial
            if (i > 0 && secaoAtual.startsWith('E ')) {
              // Esta é uma seção que começa com "E", verificar se contém "NÃO INFORMADO"
              if (contemNaoInformado(secaoAtual)) {
                console.log(`Removendo seção com "E" e "NÃO INFORMADO": "${secaoAtual}"`);
                continue; // Pular esta seção
              }
            }
            
            // Para outras seções regulares
            if (contemNaoInformado(secaoAtual)) {
              console.log(`Removendo seção regular com "NÃO INFORMADO": "${secaoAtual}"`);
              continue; // Pular esta seção
            }
            
            // Adicionar no início para manter ordem original
            resultado.unshift(secaoAtual);
          }
          
          // Reconstruir o texto com as seções restantes
          return resultado.join(', ') + '.';
        };
        
        // Usar a implementação alternativa que começa do final
        descricaoLimpa = processarConteudoDeTrasPraFrente(descricaoOriginal);
        
        // Limpar e formatar o resultado final
        descricaoLimpa = descricaoLimpa
          .replace(/,\s*,/g, ',') // Remover vírgulas duplicadas
          .replace(/^,\s*/, '') // Remover vírgulas no início
          .replace(/,\s*\./g, '.') // Remover vírgulas antes do ponto final
          .replace(/\s+/g, ' ') // Remover espaços extras
          .trim();
        
        console.log('Descrição limpa (após processamento):', descricaoLimpa);
      } catch (error) {
        console.error('Erro ao processar descrição:', error);
        descricaoLimpa = descricaoOriginal;
        // Garantir que a descrição tem um ponto final
        if (!descricaoLimpa.endsWith('.')) {
          descricaoLimpa += '.';
        }
      }
      
      // Se a descrição ficou vazia após a limpeza, usar padrão
      if (!descricaoLimpa.trim()) {
        descricaoLimpa = 'POSSUI UM VEICULO AUTOMOTOR.';
      }
      
      // Garantir que a descrição termina com ponto
      if (!descricaoLimpa.endsWith('.')) {
        descricaoLimpa += '.';
      }
      
      // Usar a descrição limpa como descrição final do veículo
      vehicleInfo.descricao = descricaoLimpa;
      
      return vehicleInfo;
    } catch (error) {
      console.error('Erro ao analisar informações do veículo:', error);
      return {
        renavam: "NÃO INFORMADO",
        descricao: "VEÍCULO AUTOMOTOR."
      };
    }
  };

  // Função para processar arquivos individuais
  const processVehicleFile = async (fileUrl, fileName) => {
    try {
      // Registrar início do processamento
      await saveLog('INICIO_PROCESSAMENTO_VEÍCULO', {
        fileUrl,
        fileName
      });
      
      // Atualizar status de processamento
      setProcessedVehicles(prev => 
        prev.map(item => 
          item.fileName === fileName 
            ? {...item, status: 'processando', mensagem: 'Processando documento...'} 
            : item
        )
      );
      
      // Atualizar detalhes de processamento
      setProcessingDetails(prev => ({...prev, [fileName]: 'Processando documento...'}));
      
      // Extrair informações do documento
      const vehicleContent = await extractDocumentInfo(fileUrl, fileName);
      
      // Atualizar status de processamento
      setProcessedVehicles(prev => 
        prev.map(item => 
          item.fileName === fileName 
            ? {...item, status: 'processando', mensagem: 'Extraindo informações do documento...'} 
            : item
        )
      );
      
      // Analisar as informações extraídas
      const vehicleInfo = parseVehicleInfo(vehicleContent);
      
      // Registrar informações extraídas
      await saveLog('INFORMAÇÕES_EXTRAÍDAS', {
        fileUrl,
        fileName,
        vehicleInfo
      });
      
      // Atualizar status de processamento
      setProcessedVehicles(prev => 
        prev.map(item => 
          item.fileName === fileName 
            ? {...item, status: 'processando', mensagem: 'Buscando valor do veículo...'} 
            : item
        )
      );
      
      // Buscar o valor do veículo
      setProcessingDetails(prev => ({...prev, [fileName]: 'Buscando valor do veículo...'}));
      const { situacao_2023, situacao_2024 } = await getVehicleValue(
        vehicleInfo.marca, 
        vehicleInfo.modelo, 
        vehicleInfo.anoFabricacao, 
        vehicleInfo.anoModelo,
        vehicleInfo.placa
      );
      
      // Atualizar status de processamento
      setProcessedVehicles(prev => 
        prev.map(item => 
          item.fileName === fileName 
            ? {...item, status: 'processando', mensagem: 'Formatando dados finais...'} 
            : item
        )
      );
      
      // Atualizar detalhes de processamento
      setProcessingDetails(prev => ({...prev, [fileName]: 'Formatando dados finais...'}));
      
      // Criar objeto do veículo
      const vehicleData = {
        grupo: "02",
        codigo: "01",
        renavam: vehicleInfo.renavam,
        descricao: vehicleInfo.descricao,
        situacao_2023: situacao_2023,
        situacao_2024: situacao_2024
      };
      
      // Registrar objeto final do veículo
      await saveLog('VEÍCULO_PROCESSADO', {
        fileUrl,
        fileName,
        vehicleData
      });
      
      return vehicleData;
    } catch (error) {
      console.error(`Erro ao processar arquivo ${fileName}:`, error);
      
      // Registrar erro no processamento
      await saveLog('ERRO_PROCESSAMENTO_VEÍCULO', {
        fileUrl,
        fileName
      }, null, error.toString());
      
      throw error;
    }
  };

  // Função para processar todos os veículos
  const processAllVehicles = async () => {
    if (files.length === 0) {
      setError("Por favor, adicione pelo menos um arquivo.");
      return;
    }

    setProcessing(true);
    setError(null);
    setProcessingDetails({});
    setLogs([]);
    
    // Limpar processamento anterior
    setProcessedVehicles([]); // Garantir que não há dados de processamento anterior
    
    // Registrar início do processamento geral
    await saveLog('INICIO_PROCESSAMENTO', {
      quantidadeArquivos: files.length,
      arquivos: files.map(f => f.name)
    });
    
    try {
      // Primeiro fazer upload de todos os arquivos
      const uploadResults = await uploadFiles();
      
      // Registrar resultado do upload
      await saveLog('UPLOAD_COMPLETO', {
        resultados: uploadResults
      });
      
      // Verificar se temos resultados de upload bem-sucedidos
      if (uploadResults.length > 0) {
        // Array para armazenar os veículos processados
        const processedVehiclesList = [];
        
        // Processar cada arquivo carregado sequencialmente
        for (const uploadedFile of uploadResults) {
          try {
            // Atualizar status de processamento
            setProcessedVehicles(prev => 
              prev.map(item => 
                item.fileName === uploadedFile.fileName 
                  ? {...item, status: 'processando', mensagem: `Processando ${uploadedFile.fileName}...`} 
                  : item
              )
            );
            
            setProcessingDetails(prev => ({
              ...prev, 
              [uploadedFile.fileName]: `Iniciando processamento de ${uploadedFile.fileName}...`
            }));
            
            // Processar arquivo para extrair informações e valores do veículo
            const vehicleData = await processVehicleFile(
              uploadedFile.fileUrl,
              uploadedFile.fileName
            );
            
            // Adicionar veículo à lista de processados
            processedVehiclesList.push(vehicleData);
            
            // Atualizar status de processamento
            setProcessedVehicles(prev => 
              prev.map(item => 
                item.fileName === uploadedFile.fileName 
                  ? {...item, status: 'concluido', mensagem: 'Veículo processado com sucesso'} 
                  : item
              )
            );
            
            // Limpar detalhes de processamento para este arquivo
            setProcessingDetails(prev => {
              const newDetails = {...prev};
              delete newDetails[uploadedFile.fileName];
              return newDetails;
            });
          } catch (error) {
            console.error(`Erro ao processar veículo ${uploadedFile.fileName}:`, error);
            
            // Atualizar status com erro
            setProcessedVehicles(prev => 
              prev.map(item => 
                item.fileName === uploadedFile.fileName 
                  ? {...item, status: 'erro', mensagem: `Erro ao processar: ${error.message}`} 
                  : item
              )
            );
            
            // Limpar detalhes de processamento para este arquivo
            setProcessingDetails(prev => {
              const newDetails = {...prev};
              delete newDetails[uploadedFile.fileName];
              return newDetails;
            });
          }
        }
        
        // Criar o JSON final formatado
        if (processedVehiclesList.length > 0) {
          const finalResult = {
            IRPFBens: [
              {
                Tasks: {
                  Veiculo: processedVehiclesList
                }
              }
            ]
          };
          
          setResult(finalResult);
          
          // Registrar JSON final
          await saveLog('JSON_FINAL', {
            quantidadeVeiculos: processedVehiclesList.length
          }, finalResult);
        }
      }
    } catch (err) {
      setError(`Erro ao processar veículos: ${err.message}`);
      
      // Registrar erro geral
      await saveLog('ERRO_GERAL', {
        mensagem: err.message
      }, null, err.toString());
    } finally {
      // Registrar finalização do processamento
      await saveLog('FIM_PROCESSAMENTO', {
        status: error ? 'Erro' : 'Sucesso',
        tempoTotal: `${new Date().toISOString()}`
      });
      
      setProcessing(false);
      setProcessingDetails({});
    }
  };

  // Função para copiar o JSON de resultado
  const copyResultToClipboard = () => {
    if (!result) return;
    
    const resultString = JSON.stringify(result, null, 2);
    navigator.clipboard.writeText(resultString);
    alert("JSON copiado para a área de transferência!");
  };

  // Função para baixar o JSON como arquivo
  const downloadJsonFile = () => {
    if (!result) return;
    
    const resultString = JSON.stringify(result, null, 2);
    const blob = new Blob([resultString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Veiculos.json';
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="container mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Link to={createPageUrl("Operations")}>
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <Car className="h-8 w-8 text-emerald-600" />
            <h1 className="text-3xl font-bold text-gray-900">Bens e Direitos {'>'} Veículos</h1>
          </div>
        </div>
        <p className="text-gray-500">
          Esta ferramenta permite cadastrar veículos para declaração de bens e direitos no Imposto de Renda Pessoa Física (IRPF). Cada veículo será processado individualmente.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Gerenciamento de Veículos</CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm mb-6">
                  <p className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    <span>{error}</span>
                  </p>
                </div>
              )}

              <div 
                className={`
                  border-2 border-dashed rounded-lg p-4 mb-6
                  transition-colors duration-200 ease-in-out
                  text-center flex flex-col items-center justify-center
                  mx-auto w-full cursor-pointer
                  ${dragActive 
                    ? 'border-emerald-400 bg-emerald-50' 
                    : 'border-gray-300 hover:border-gray-400'
                  }
                `}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={handleButtonClick}
                style={{ height: "120px" }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileInput}
                  disabled={uploading}
                />
                
                <div className="flex flex-col items-center justify-center h-full gap-2">
                  <Car className="w-20 h-20 text-emerald-600 mx-auto mb-2" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleButtonClick();
                    }}
                    disabled={uploading}
                    className="gap-1"
                  >
                    <Upload className="h-3 w-3" />
                    Selecionar Arquivos
                  </Button>
                  <p className="text-xs text-gray-500 mt-1">
                    Arraste e solte arquivos aqui ou clique para selecionar
                  </p>
                </div>
              </div>

              {files.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-800">Arquivos para Upload:</h3>
                  <div className="space-y-3">
                    {files.map((file, index) => (
                      <div 
                        key={index} 
                        className={`flex items-center justify-between border rounded-md p-3`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-gray-100 rounded-md">
                            {file.type.includes('pdf') 
                              ? <FileText className="h-6 w-6 text-blue-500" />
                              : file.type.includes('image') 
                                ? <FileText className="h-6 w-6 text-green-500" />
                                : <File className="h-6 w-6 text-gray-500" />
                            }
                          </div>
                          
                          <div>
                            <p className="font-medium text-sm">{file.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {uploadProgress[file.name] !== undefined && uploadProgress[file.name] < 100 && (
                            <div className="w-24">
                              <Progress value={uploadProgress[file.name]} className="h-2" />
                            </div>
                          )}
                          
                          {uploadProgress[file.name] === 100 ? (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          ) : (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-500 hover:bg-red-50"
                              onClick={() => removeFile(file.name)}
                              disabled={uploading}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detalhes de processamento */}
              {Object.keys(processingDetails).length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800 text-sm mb-6">
                  <p className="font-medium mb-2">Progresso do processamento:</p>
                  <ul className="space-y-1">
                    {Object.entries(processingDetails).map(([key, value]) => (
                      <li key={key} className="flex items-center">
                        <Loader2 className="h-3 w-3 animate-spin mr-2" />
                        {value}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                {files.length > 0 && (
                  <Button 
                    type="button"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                    onClick={processAllVehicles}
                    disabled={processing || uploading}
                  >
                    {processing || uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {uploading ? 'Fazendo Upload...' : 'Processando...'}
                      </>
                    ) : (
                      <>
                        <Car className="h-4 w-4" />
                        Processar Veículos
                      </>
                    )}
                  </Button>
                )}
              </div>

              {processedVehicles.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-semibold text-gray-800 mb-4">Resultado do Processamento:</h3>
                  <div className="space-y-2">
                    {processedVehicles.map((item, index) => (
                      <div 
                        key={index} 
                        className={`flex items-center justify-between p-3 rounded-md ${
                          item.status === 'concluido' 
                            ? 'bg-green-50 border border-green-200' 
                            : item.status === 'erro' 
                              ? 'bg-red-50 border border-red-200' 
                              : item.status === 'processando' 
                                ? 'bg-blue-50 border border-blue-200' 
                                : 'bg-gray-50 border border-gray-100'
                        }`}
                      >
                        <div className="flex gap-3 items-center">
                          {item.status === 'concluido' ? (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          ) : item.status === 'erro' ? (
                            <AlertCircle className="h-5 w-5 text-red-500" />
                          ) : item.status === 'processando' ? (
                            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                          ) : (
                            <FileText className="h-5 w-5 text-gray-400" />
                          )}
                          <div>
                            <p className="text-sm font-medium">{item.fileName}</p>
                            <p className="text-xs text-gray-600">{item.mensagem}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {processing && (
                <div className="flex flex-col gap-4 mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
                    <h3 className="text-lg font-medium">Processando...</h3>
                  </div>
                </div>
              )}

              {result && (
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold text-gray-800">JSON de Saída:</h3>
                    <div className="flex gap-2">
                      <Button 
                        size="sm"
                        variant="ghost" 
                        className="flex items-center gap-1 text-blue-600" 
                        onClick={copyResultToClipboard}
                      >
                        <Copy className="h-4 w-4" /> Copiar
                      </Button>
                      <Button 
                        size="sm"
                        variant="ghost" 
                        className="flex items-center gap-1 text-green-600" 
                        onClick={downloadJsonFile}
                      >
                        <Download className="h-4 w-4" /> Baixar
                      </Button>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-sm text-gray-900">
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle>Informações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800">O que é isso?</h3>
                <p className="text-sm text-gray-600">
                  Esta ferramenta permite cadastrar veículos para declaração de bens e direitos no Imposto de Renda Pessoa Física (IRPF). Cada veículo será processado individualmente.
                </p>
                
                <h3 className="font-semibold text-gray-800">Como usar</h3>
                <ol className="text-sm text-gray-600 space-y-2 list-decimal ml-4">
                  <li>Faça upload dos documentos dos veículos (documento de propriedade, nota fiscal, etc.)</li>
                  <li>Clique em "Processar Veículos" para iniciar o processamento dos arquivos</li>
                  <li>Após o processamento, você verá o resultado em formato JSON</li>
                  <li>Use o botão "Copiar" para copiar o JSON ou "Baixar" para fazer o download do arquivo</li>
                </ol>

                <h3 className="font-semibold text-gray-800">Tipos de documentos aceitos</h3>
                <ul className="text-sm text-gray-600 space-y-1 list-disc ml-4">
                  <li>Documentos de propriedade (CRLV, CRV)</li>
                  <li>Notas fiscais de compra</li>
                  <li>Contratos de compra e venda</li>
                  <li>Recibos de compra</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
